import React, { ReactNode, useState, useEffect, RefObject } from 'react';
import classNames from 'classnames';
import { usePopper } from 'react-popper';
import { Placement } from '@popperjs/core/lib/enums';
import { Portal } from '@headlessui/react';

interface TooltipLightProps {
  content: ReactNode;
  children: ReactNode;
  enabled?: boolean;
  placement?: Placement;
  className?: string;
  offsetX?: number; // horizontal skidding in px (negative moves left)
  anchorRef?: RefObject<HTMLElement>; // if provided, anchor the popper to this element
  interactive?: boolean;
  onVisibilityChange?: (isVisible: boolean) => void;
}

const TooltipLight: React.FC<TooltipLightProps> = ({
  content,
  children,
  enabled = true,
  placement = 'top',
  className,
  offsetX = 0,
  anchorRef,
  interactive = false,
  onVisibilityChange,
}) => {
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  const actualReference = (anchorRef?.current as any) || referenceElement;

  const { styles, attributes } = usePopper(actualReference, popperElement, {
    placement: (placement === 'bottom'
      ? 'bottom-start'
      : placement) as Placement,
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [offsetX, 8],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          padding: 8,
          altAxis: true,
        },
      },
    ],
  });

  // Handle smooth fade animation
  useEffect(() => {
    if (onVisibilityChange) {
      onVisibilityChange(isVisible);
    }
    if (isVisible) {
      setShouldRender(true);
    } else {
      // Delay removal to allow fade-out animation
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 200); // Match transition duration
      return () => clearTimeout(timer);
    }
  }, [isVisible, onVisibilityChange]);

  return (
    <div
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      <div ref={setReferenceElement}>{children}</div>
      {enabled && shouldRender && (
        <Portal>
          <div
            ref={setPopperElement}
            style={styles.popper}
            {...attributes.popper}
            className={classNames(
              'text-neutral-700 relative z-50 rounded-md border border-neutral-75 bg-white px-2 py-1 text-small-caption font-normal shadow-lg transition-opacity duration-200 ease-in-out',
              isVisible ? 'opacity-100' : 'opacity-0',
              className,
            )}
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={() => {
              if (interactive) setIsVisible(true);
            }}
            onMouseLeave={() => {
              if (interactive) setIsVisible(false);
            }}
          >
            {content}
          </div>
        </Portal>
      )}
    </div>
  );
};

export default TooltipLight;
